import React, { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { App as AntApp, Button, message as rawMessage, notification as rawNotification } from 'antd';
import AppTheme from '../components/theme/AppTheme';
import { registerSessionReset, sessionIsCurrent, sessionTicket } from './sessionLifecycle';
import { createActionableNoticeStore } from './actionableNoticeStore';
import './actionableNotifications.css';

const store = createActionableNoticeStore(), wrappers = new WeakMap();
export const dismissActionableNotifications = () => store.dismissAll();
// Logout clears UI without acknowledging another user's mailbox work.
registerSessionReset(() => store.dismissAll(false));

const sessionApis = new Set();
let popupSequence = 0;
const nextPopupKey = () => `session-notice-${++popupSequence}`;
const ignoredMessage = () => { const close = () => {}; close.then = (...args) => Promise.resolve().then(...args); return close; };

// Capture the originating session, never "whichever user is logged in when a
// delayed callback finally runs". All APIs enroll automatically, not by opt-in.
function sessionApi(raw, kind, ticket) {
  const handles = new Set(), keys = new Set();
  const target = kind === 'notification' ? withNotificationActions(raw) : raw;
  let active = true, unregister = () => {};
  const current = () => active && sessionIsCurrent(ticket);
  const clear = () => {
    keys.forEach(key => target?.destroy?.(key)); keys.clear();
    handles.forEach(close => { try { close(); } catch { } }); handles.clear();
  };
  const dispose = () => {
    if (!active) return;
    active = false; unregister(); sessionApis.delete(dispose);
    clear();
  };
  dispose.kind = kind; dispose.clear = clear;
  unregister = registerSessionReset(dispose); sessionApis.add(dispose);
  const api = { dispose, activate() {
    // React StrictMode replays effect setup after cleanup, without a new render.
    if (!active && sessionIsCurrent(ticket)) { active = true; unregister = registerSessionReset(dispose); sessionApis.add(dispose); }
  }, destroy(key) {
    if (!current()) return;
    if (key == null) clear();
    else { keys.delete(key); target?.destroy?.(key); }
  } };
  for (const method of ['open', 'info', 'success', 'warning', 'warn', 'error', 'loading']) {
    if (typeof target?.[method] !== 'function') continue;
    api[method] = (...args) => {
      if (!current()) return kind === 'message' ? ignoredMessage() : undefined;
      if (kind === 'notification') {
        const config = args[0], key = config.key ?? nextPopupKey();
        keys.add(key);
        return target[method]({ ...config, key, onClose: () => { keys.delete(key); if (current()) config.onClose?.(); },
          onClick: event => { if (current()) config.onClick?.(event); } });
      }
      const next = args.map(value => typeof value === 'function' ? (...values) => { if (current()) value(...values); } : value);
      const first = next[0];
      if (first && typeof first === 'object' && 'content' in first) {
        const callback = first.onClose;
        next[0] = { ...first, onClose: () => { if (current()) callback?.(); } };
        if (first.key != null) keys.add(first.key);
      }
      const close = target[method](...next);
      if (typeof close === 'function') { handles.add(close); close.then?.(() => handles.delete(close)); }
      return close;
    };
  }
  if (kind === 'notification') wrappers.set(api, api);
  return api;
}
export const createSessionNotificationApi = (raw = rawNotification, ticket = sessionTicket()) => sessionApi(raw, 'notification', ticket);
export const createSessionMessageApi = (raw = rawMessage, ticket = sessionTicket()) => sessionApi(raw, 'message', ticket);
export function useSessionNotification(config) {
  const [raw, holder] = rawNotification.useNotification(config), ticket = useRef(sessionTicket());
  const api = useMemo(() => createSessionNotificationApi(raw, ticket.current), [raw]);
  useEffect(() => { api.activate(); return () => api.dispose(); }, [api]);
  return [api, holder];
}
export function useSessionMessage(config) {
  const [raw, holder] = rawMessage.useMessage(config), ticket = useRef(sessionTicket());
  const api = useMemo(() => createSessionMessageApi(raw, ticket.current), [raw]);
  useEffect(() => { api.activate(); return () => api.dispose(); }, [api]);
  return [api, holder];
}
export function useSessionMessageApi() {
  const ticket = useRef(sessionTicket());
  const api = useMemo(() => createSessionMessageApi(rawMessage, ticket.current), []);
  useEffect(() => { api.activate(); return () => api.dispose(); }, [api]);
  return api;
}
export function useSessionApp() {
  const raw = AntApp.useApp(), ticket = useRef(sessionTicket());
  const notification = useMemo(() => createSessionNotificationApi(raw.notification, ticket.current), [raw.notification]);
  const message = useMemo(() => createSessionMessageApi(raw.message, ticket.current), [raw.message]);
  useEffect(() => { notification.activate(); message.activate(); return () => { notification.dispose(); message.dispose(); }; }, [notification, message]);
  return { ...raw, notification, message };
}
export function destroySessionMessages() { sessionApis.forEach(dispose => { if (dispose.kind === 'message') dispose.clear(); }); rawMessage?.destroy?.(); }
export function destroySessionPopups() {
  [...sessionApis].forEach(dispose => dispose()); store.dismissAll(false);
  rawNotification?.destroy?.(); rawMessage?.destroy?.();
}

function NotificationActions({ id, placement, children }) {
  const latest = useSyncExternalStore(store.subscribe, () => store.latest(placement));
  const rightAligned = placement === 'topRight' || placement === 'bottomRight';
  const dismiss = latest === id && <Button size="small" onClick={event => { event.stopPropagation(); store.dismissAll(); }}>Dismiss all</Button>;
  return <AppTheme><div role="group" aria-label="Notification actions" className={`srm-notification-action-row${rightAligned ? ' srm-notification-action-row-right' : ''}`}>
    {!rightAligned && dismiss}
    <div className="srm-notification-primary-actions">{children}</div>
    {rightAligned && dismiss}
  </div></AppTheme>;
}

// Preserve the original API/keys/timers and callbacks. Do not reopen older
// notices just to move the button: their footer subscribes to the live stack.
export function withNotificationActions(api) {
  if (wrappers.has(api)) return wrappers.get(api);
  const current = new Map();
  const keys = { sequence: 0 };
  const result = { ...api };
  for (const method of ['open', 'info', 'success', 'warning', 'error']) {
    if (!api[method]) continue;
    result[method] = config => {
      const actions = config.actions ?? config.btn;
      if (!actions) { current.get(config.key)?.remove(false); return api[method](config); } // Button-free snackbar: untouched.
      const key = config.key ?? `actionable-notice-${++keys.sequence}`;
      current.get(key)?.remove(false);
      const id = Symbol(), placement = config.placement || 'topRight';
      const entry = {
        remove(notify) {
          if (current.get(key) !== entry) return;
          current.delete(key); store.remove(id);
          if (notify) config.onClose?.();
        },
        dismiss(notify = true) { try { entry.remove(notify); } finally { api.destroy(key); } },
      };
      current.set(key, entry); store.add(id, { placement, dismiss: entry.dismiss });
      const wrapped = { ...config, key, className: [config.className, 'srm-actionable-notification'].filter(Boolean).join(' '), onClose: () => entry.remove(true) };
      delete wrapped.btn; // Accept legacy callers without passing Ant's deprecated prop.
      wrapped.actions = <NotificationActions id={id} placement={placement}>{actions}</NotificationActions>;
      return api[method](wrapped);
    };
  }
  result.destroy = key => {
    if (key === undefined) [...current.values()].forEach(entry => entry.remove(false));
    else current.get(key)?.remove(false);
    return api.destroy(key);
  };
  wrappers.set(api, result); wrappers.set(result, result);
  return result;
}
